/**
 * @description 处理打开文件的逻辑
 */

import {
	type LyricLine,
	parseEslrc,
	parseLys,
	parseQrc,
	parseYrc,
} from "@applemusic-like-lyrics/lyric";
import { openDB } from "idb";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { uid } from "uid";
import { audioEngine } from "$/modules/audio/audio-engine";
import { getProjectList } from "$/modules/project/autosave/autosave";
import { isProjectMatch } from "$/modules/project/logic/project-match";
import { parseLyric as parseTTML } from "$/modules/project/logic/ttml-parser";
import { getSuggestedTtmlFileName } from "$/modules/project/logic/metadata-filename";
import { confirmDialogAtom } from "$/states/dialogs.ts";
import { pushNotificationAtom } from "$/states/notifications";
import {
	fileUpdateSessionAtom,
	isDirtyAtom,
	newLyricLinesAtom,
	projectIdAtom,
	saveFileNameAtom,
} from "$/states/main.ts";
import type { TTMLLyric } from "$/types/ttml";
import { log, error as logError } from "$/utils/logging.ts";
import { parseLrc } from "$/utils/parse-lrc";

const LYRIC_PARSERS: Record<string, (text: string) => LyricLine[]> = {
	lrc: parseLrc,
	eslrc: parseEslrc,
	qrc: parseQrc,
	yrc: parseYrc,
	lys: parseLys,
};

const AUDIO_EXTENSIONS = new Set([
	"opus",
	"flac",
	"webm",
	"weba",
	"wav",
	"ogg",
	"m4a",
	"oga",
	"mid",
	"mp3",
	"aiff",
	"wma",
	"au",
]);

const AUDIO_CACHE_DB = "amll-audio-cache";
const AUDIO_CACHE_STORE = "audio-files";
const AUDIO_CACHE_KEY = "last-audio";

type AudioCacheRecord = {
	key: string;
	file: Blob;
	name: string;
	type: string;
	updatedAt: number;
};

const audioCacheDbPromise = openDB(AUDIO_CACHE_DB, 1, {
	upgrade(db) {
		if (!db.objectStoreNames.contains(AUDIO_CACHE_STORE)) {
			db.createObjectStore(AUDIO_CACHE_STORE, { keyPath: "key" });
		}
	},
});

const readAudioCache = async () => {
	try {
		const db = await audioCacheDbPromise;
		return (await db.get(AUDIO_CACHE_STORE, AUDIO_CACHE_KEY)) as
			| AudioCacheRecord
			| undefined;
	} catch {
		return undefined;
	}
};

const writeAudioCache = async (file: File) => {
	try {
		const db = await audioCacheDbPromise;
		const payload: AudioCacheRecord = {
			key: AUDIO_CACHE_KEY,
			file,
			name: file.name,
			type: file.type,
			updatedAt: Date.now(),
		};
		await db.put(AUDIO_CACHE_STORE, payload);
	} catch {}
};

export const useFileOpener = () => {
	const setNewLyricLines = useSetAtom(newLyricLinesAtom);
	const setProjectId = useSetAtom(projectIdAtom);
	const setSaveFileName = useSetAtom(saveFileNameAtom);
	const setConfirmDialog = useSetAtom(confirmDialogAtom);
	const isDirty = useAtomValue(isDirtyAtom);
	const fileUpdateSession = useAtomValue(fileUpdateSessionAtom);
	const { t } = useTranslation();
	const setPushNotification = useSetAtom(pushNotificationAtom);

	const normalizeLyricLines = useCallback(
		(lyricLines: LyricLine[]): TTMLLyric => {
			return {
				lyricLines: lyricLines.map((line) => ({
					...line,
					words: line.words.map((word) => ({
						...word,
						id: uid(),
						obscene: false,
						emptyBeat: 0,
					})),
					ignoreSync: false,
					id: uid(),
				})),
				metadata: [],
			};
		},
		[],
	);

	const performOpenFile = useCallback(
		async (file: File, forceExt?: string) => {
			const rawExt = file.name.split(".").pop()?.toLowerCase() || "";
			const ext = forceExt ? forceExt.toLowerCase() : rawExt;

			try {
				if (AUDIO_EXTENSIONS.has(ext)) {
					audioEngine.loadMusic(file);
					await writeAudioCache(file);
					return;
				}

				let lyricData: TTMLLyric | null = null;
				const text = await file.text();

				if (ext === "ttml") {
					lyricData = parseTTML(text);
				} else if (ext in LYRIC_PARSERS) {
					const parser = LYRIC_PARSERS[ext];
					const rawLines = parser(text);
					lyricData = normalizeLyricLines(rawLines);
				} else {
					setPushNotification({
						title: t("error.unsupportedFileFormat", "不支持的文件格式: {ext}", {
							ext,
						}),
						level: "error",
						source: "useFileOpener",
					});
					return;
				}

				if (!lyricData) return;

				let resolvedProjectId = uid();

				try {
					if (lyricData.metadata.length > 0) {
						const projects = await getProjectList();
						const matchedProject = projects.find((p) =>
							isProjectMatch(p, lyricData as TTMLLyric),
						);

						if (matchedProject) {
							log(
								`匹配到了已有项目: ${matchedProject.name} (${matchedProject.id})`,
							);
							resolvedProjectId = matchedProject.id;
						} else {
							log("未匹配已有项目");
						}
					}
				} catch (e) {
					logError("解析项目数据时失败", e);
				}

				setProjectId(resolvedProjectId);
				setNewLyricLines(lyricData);
				const suggestedFile = getSuggestedTtmlFileName(lyricData.metadata);
				const nextFileName =
					ext === "ttml" ? file.name : (suggestedFile?.fileName ?? file.name);
				setSaveFileName(nextFileName);
			} catch (e) {
				logError(`Failed to open file: ${file.name}`, e);
				setPushNotification({
					title: t("error.openFileFailed", "打开文件失败"),
					level: "error",
					source: "useFileOpener",
				});
			}
		},
		[
			setNewLyricLines,
			setProjectId,
			setSaveFileName,
			normalizeLyricLines,
			t,
			setPushNotification,
		],
	);

	const openFile = useCallback(
		/**
		 * 打开文件
		 * @param file
		 * @param forceExt 可选参数，用于强制指定解析方式，不传入则从文件后缀名推断
		 */
		(file: File, forceExt?: string) => {
			const run = () => performOpenFile(file, forceExt);

			const rawExt = file.name.split(".").pop()?.toLowerCase() || "";
			const finalExt = forceExt || rawExt;

			if (AUDIO_EXTENSIONS.has(finalExt)) {
				run();
				return;
			}

			if (fileUpdateSession) {
				setConfirmDialog({
					open: true,
					title: "确认进入编辑",
					description: `当前处于 PR #${fileUpdateSession.prNumber} 更新会话。是否继续打开文件并进入编辑？`,
					onConfirm: run,
				});
				return;
			}

			if (isDirty) {
				setConfirmDialog({
					open: true,
					title: t("confirmDialog.openFile.title", "确认打开文件"),
					description: t(
						"confirmDialog.openFile.description",
						"当前文件有未保存的更改。如果继续，这些更改将会丢失。确定要打开新文件吗？",
					),
					onConfirm: run,
				});
			} else {
				run();
			}
		},
		[
			fileUpdateSession,
			isDirty,
			performOpenFile,
			setConfirmDialog,
			t,
		],
	);

	const openCachedAudio = useCallback(async () => {
		try {
			const record = await readAudioCache();
			if (!record?.file) {
				setPushNotification({
					title: t("audioPanel.cachedAudioMissing", "未找到缓存音频"),
					level: "warning",
					source: "useFileOpener",
				});
				return;
			}
			const name = record.name || "cached-audio";
			const type = record.type || record.file.type || "audio/*";
			const file = new File([record.file], name, { type });
			audioEngine.loadMusic(file);
			setPushNotification({
				title: t("audioPanel.cachedAudioLoaded", "已从缓存加载音频"),
				level: "success",
				source: "useFileOpener",
			});
		} catch (error) {
			setPushNotification({
				title: t("audioPanel.cachedAudioFailed", "读取缓存音频失败"),
				level: "error",
				source: "useFileOpener",
			});
		}
	}, [setPushNotification, t]);

	return { openFile, openCachedAudio };
};
